import { AppStrings } from "../../../utils/constants/AppStrings";
import { convertToUserTimezone } from "../../../utils/dateService";

const INTERNAL_EMAIL_SUFFIXES = ["@trivelta.com", "@rebet.app"];

const DEPOSIT_TYPES = new Set(["cash_in", "deposit", "credit"]);
const WITHDRAW_TYPES = new Set(["cash_out", "withdraw", "debit"]);

const REPORT_DATETIME_FORMAT = "MM/DD/YYYY HH:mm:ss";

/**
 * Format `created_at` for display. Accepts unix seconds (AMT-494) or
 * pre-formatted date strings from older payloads. Uses the admin timezone.
 */
export function formatReportDateTime(createdAt) {
  if (createdAt == null || createdAt === "") return "—";

  const formatted = convertToUserTimezone(createdAt, REPORT_DATETIME_FORMAT);
  if (!formatted || formatted === "-") return "—";
  return formatted;
}

export function formatReportAmount(amount) {
  if (amount == null || amount === "") return "—";
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return String(amount);
  return Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.round(numeric * 100) / 100);
}

/** Hide internal operator emails from the Email column. */
export function formatReportEmail(email) {
  if (email == null || email === "") return "—";
  const normalized = String(email).trim().toLowerCase();
  if (INTERNAL_EMAIL_SUFFIXES.some((suffix) => normalized.endsWith(suffix))) {
    return "—";
  }
  return String(email);
}

/**
 * Map payment_type → Deposit / Withdraw. Falls back to the raw value when
 * unrecognized (or to legacy `type` via the caller).
 */
export function mapPaymentTypeLabel(paymentType) {
  if (paymentType == null || paymentType === "") return "—";
  const key = String(paymentType).trim().toLowerCase();
  if (DEPOSIT_TYPES.has(key)) return AppStrings.DEPOSIT;
  if (WITHDRAW_TYPES.has(key)) return AppStrings.PAM_REPORT_TYPE_WITHDRAW;
  return String(paymentType);
}

export function isDepositPaymentType(paymentType) {
  if (paymentType == null) return false;
  return DEPOSIT_TYPES.has(String(paymentType).trim().toLowerCase());
}

/**
 * Map payment_status → Approved / needs_review / Rejected (badge label).
 */
export function mapPaymentStatusLabel(paymentStatus) {
  if (paymentStatus == null || paymentStatus === "") return "—";
  const key = String(paymentStatus).trim().toLowerCase();
  if (key === "succeeded" || key === "approved") {
    return AppStrings.PAM_REPORT_STATUS_APPROVED;
  }
  if (key === "needs_review" || key === "needs-review" || key === "in review") {
    return AppStrings.PAM_REPORT_STATUS_NEEDS_REVIEW;
  }
  if (key === "failed" || key === "rejected" || key === "declined") {
    return AppStrings.PAM_REPORT_STATUS_REJECTED;
  }
  if (key === "processing" || key === "pending") {
    return AppStrings.STATUS_PENDING;
  }
  if (
    key === "requires_action" ||
    key === "requires-action" ||
    key === "requires action"
  ) {
    return AppStrings.PAM_REPORT_STATUS_REQUIRES_ACTION;
  }
  return String(paymentStatus);
}

export function getPaymentStatusModifierKey(paymentStatus) {
  const label = mapPaymentStatusLabel(paymentStatus);
  if (label === AppStrings.PAM_REPORT_STATUS_APPROVED) return "approved";
  if (label === AppStrings.PAM_REPORT_STATUS_NEEDS_REVIEW) {
    return "needs-review";
  }
  if (label === AppStrings.PAM_REPORT_STATUS_REJECTED) return "rejected";
  if (label === AppStrings.STATUS_PENDING) return "pending";
  if (label === AppStrings.PAM_REPORT_STATUS_REQUIRES_ACTION) {
    return "requires-action";
  }
  return null;
}

/** Display money_type as-is until client-specific label maps land. */
export function formatMoneyTypeLabel(moneyType) {
  if (moneyType == null || moneyType === "") return "—";
  return String(moneyType);
}

export function displayOrDash(value) {
  if (value == null || value === "") return "—";
  return String(value);
}
